import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import './CartPage.css';

const CartPage: React.FC = () => {
  const { items, total, updateQuantity, removeItem, clearCart } = useCart();
  const navigate = useNavigate();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(price);
  };

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    if (newQuantity > 0) {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleRemoveItem = (productId: string) => {
    removeItem(productId);
  };

  const handleClearCart = () => {
    clearCart();
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  if (items.length === 0) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="empty-cart">
            <div className="empty-cart-icon">
              <span>cart</span>
            </div>
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added any products to your cart yet.</p>
            <div className="empty-cart-actions">
              <Link to="/products" className="btn btn-primary btn-large">
                Continue Shopping
              </Link>
              <Link to="/custom-order" className="btn btn-outline btn-large">
                Request Custom Order
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">
        <div className="cart-header">
          <h1>Shopping Cart</h1>
          <p>You have {items.length} {items.length === 1 ? 'item' : 'items'} in your cart</p>
        </div>

        <div className="cart-content">
          <div className="cart-items">
            {items.map((item) => (
              <div key={item.product.id} className="cart-item">
                <div className="item-image">
                  <img 
                    src={item.product.imageUrl || ''} 
                    alt={item.product.name}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </div>
                
                <div className="item-details">
                  <h3>{item.product.name}</h3>
                  <p className="item-description">{item.product.description}</p>
                  <div className="item-price">{formatPrice(item.product.price)}</div>
                </div>

                <div className="item-quantity">
                  <div className="quantity-controls">
                    <button 
                      className="quantity-btn"
                      onClick={() => handleQuantityChange(item.product.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      -
                    </button>
                    <span className="quantity-display">{item.quantity}</span>
                    <button 
                      className="quantity-btn"
                      onClick={() => handleQuantityChange(item.product.id, item.quantity + 1)}
                      disabled={item.quantity >= item.product.stock}
                    >
                      +
                    </button>
                  </div>
                  {item.quantity >= item.product.stock && (
                    <span className="stock-warning">Max stock reached</span>
                  )}
                </div>

                <div className="item-total">
                  <div className="total-price">
                    {formatPrice(item.product.price * item.quantity)}
                  </div>
                  <button 
                    className="remove-btn"
                    onClick={() => handleRemoveItem(item.product.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <div className="summary-card">
              <h3>Order Summary</h3>
              
              <div className="summary-row">
                <span>Subtotal ({items.length} items)</span>
                <span>{formatPrice(total)}</span>
              </div>
              
              <div className="summary-row">
                <span>Delivery Charges</span>
                <span>Calculated at checkout</span>
              </div>
              
              <div className="summary-divider"></div>
              
              <div className="summary-row total">
                <span>Total</span>
                <span>{formatPrice(total)}</span>
              </div>

              <div className="summary-note">
                <p>Delivery charges may apply. You can also pick up your order from our office. Final confirmation will be shared via your mobile number.</p>
              </div>

              <div className="summary-actions">
                <button
                  onClick={handleCheckout}
                  className="btn btn-primary btn-large btn-full"
                >
                  Proceed to Checkout
                </button>
                
                <button
                  onClick={handleClearCart}
                  className="btn btn-outline btn-large btn-full"
                >
                  Clear Cart
                </button>
                
                <Link to="/products" className="btn btn-secondary btn-large btn-full">
                  Continue Shopping
                </Link>
              </div>
            </div>

            <div className="help-card">
              <h4>Need Help?</h4>
              <p>Call us: +91 98765 43210</p>
              <p>Email: info@jkprints.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
